import { useState } from "react";
import {
  Mail,
  UserX,
  MoreHorizontal,
  Trash2,
  AlertTriangle,
  UserCheck,
  UserMinus,
} from "lucide-react";
import {
  deleteSubscriptionUser,
  revokeSubscription,
} from "../../../Services/subscriptionService";
import { toast } from "react-toastify";

export function UserList({ users, getSubscriptionDataAndCount }) {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: true,
    right: true,
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Accepted":
        return "bg-green-100 text-green-800";
      case "Expired":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const toggleDropdown = (userId, event = null) => {
    if (activeDropdown === userId) {
      setActiveDropdown(null);
      return;
    }

    // Default position
    let position = { top: true, right: true };

    // Calculate position based on viewport if event is available
    if (event && event.target) {
      try {
        const rect = event.target.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Check if there's enough space below
        const spaceBelow = viewportHeight - rect.bottom;
        const dropdownHeight = 160; // Increased height for additional email option

        // Check if there's enough space to the right
        const spaceRight = viewportWidth - rect.right;
        const dropdownWidth = 200; // Width of dropdown

        position = {
          top: spaceBelow >= dropdownHeight,
          right: spaceRight >= dropdownWidth,
        };
      } catch (error) {
        console.warn("Error calculating dropdown position:", error);
        // Use default position if calculation fails
      }
    }

    setDropdownPosition(position);
    setActiveDropdown(userId);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const handleActivateClick = (user) => {
    setSelectedUser(user);
    setShowActivateModal(true);
    setActiveDropdown(null);
  };

  // New function to handle sending email
  const handleSendEmail = (user) => {
    try {
      // Create mailto link with user's email
      const mailtoLink = `mailto:${user.email}?subject=Regarding Your Subscription&body=Hello ${user.name},%0D%0A%0D%0A`;

      // Open the default email client
      window.location.href = mailtoLink;

      // Close the dropdown
      setActiveDropdown(null);

      // Optional: Show success message
      toast.success(`Email client opened for ${user.name}`);
    } catch (error) {
      console.error("Error opening email client:", error);
      toast.error("Failed to open email client");
    }
  };

  // Fixed: Now properly calls handleDeleteUser instead of handleDeactivateUser
  const handleConfirmModal = async () => {
    if (!selectedUser) return;

    setIsDeleting(true);
    try {
      await handleDeleteUser(selectedUser.id);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete user");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setSelectedUser(null);
    }
  };

  const handleConfirmActivateModal = async () => {
    if (!selectedUser) return;

    setIsProcessing(true);
    try {
      if (selectedUser.status === true) {
        // User is active, so deactivate
        await handleDeactivateUser(selectedUser.id);
      } else {
        // User is inactive, so activate
        await handleActivateUser(selectedUser.id);
      }
    } catch (error) {
      console.error("Status change failed:", error);
      toast.error("Failed to change user status");
    } finally {
      setIsProcessing(false);
      setShowActivateModal(false);
      setSelectedUser(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  const handleActivateCancel = () => {
    setShowActivateModal(false);
    setSelectedUser(null);
  };

  // This function deactivates/revokes user subscription (soft delete)
  const handleDeactivateUser = async (userId) => {
    try {
      const revokeResponse = await revokeSubscription(userId, {
        subscriptionRevokeStatus: true,
      });
      if (revokeResponse.success) {
        toast.success("User deactivated successfully");
        getSubscriptionDataAndCount();
      } else {
        toast.error("Failed to deactivate user");
      }
    } catch (error) {
      console.error("Error deactivating user:", error);
      toast.error("An error occurred while deactivating user");
    }
  };

  // This function activates user subscription
  const handleActivateUser = async (userId) => {
    try {
      const activateResponse = await revokeSubscription(userId, {
        subscriptionRevokeStatus: false,
      });
      if (activateResponse.success) {
        toast.success("User activated successfully");
        getSubscriptionDataAndCount();
      } else {
        toast.error("Failed to activate user");
      }
    } catch (error) {
      console.error("Error activating user:", error);
      toast.error("An error occurred while activating user");
    }
  };

  // Fixed: Properly implemented delete function (hard delete)
  const handleDeleteUser = async (userId) => {
    try {
      // Call the delete service - removed the second parameter as it seems incorrect for delete
      const deleteResponse = await deleteSubscriptionUser(userId);

      if (deleteResponse && deleteResponse.success) {
        toast.success("User deleted successfully");
        // Refresh the user list after successful deletion
        if (getSubscriptionDataAndCount) {
          getSubscriptionDataAndCount();
        }
      } else {
        // Handle case where response doesn't have success property or success is false
        const errorMessage = deleteResponse?.message || "Failed to delete user";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      // Show more specific error message if available
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred while deleting user";
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <div className="overflow-visible rounded-lg border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  Phone
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users?.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    No users have been invited yet.
                  </td>
                </tr>
              ) : (
                users?.map((user) => (
                  <tr key={user.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{user.name}</td>
                    <td className="px-4 py-3 text-sm">{user.email}</td>
                    <td className="px-4 py-3 text-sm">{user.phoneNumber}</td>
                    <td className="px-4 py-3 text-sm">
                      {user.role === "1"
                        ? "User"
                        : user.role === "0"
                        ? "Admin"
                        : user.role === "2"
                        ? "Guest"
                        : "Not Defined"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(
                          user.status
                        )}`}
                      >
                        {user.status === true ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right relative">
                      <button
                        className="p-1 rounded-md hover:bg-gray-100"
                        onClick={(e) => toggleDropdown(user.id, e)}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                      </button>
                      {activeDropdown === user.id && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setActiveDropdown(null)}
                          ></div>
                          <div
                            className={`absolute w-48 bg-white rounded-md shadow-lg z-20 border ${
                              dropdownPosition.top ? "top-8" : "bottom-8"
                            } ${dropdownPosition.right ? "right-0" : "left-0"}`}
                          >
                            <div className="py-1">
                              <div className="px-4 py-2 text-xs font-semibold text-gray-500">
                                Actions
                              </div>
                              <div className="h-px bg-gray-200 my-1"></div>

                              {/* Send Email Button */}
                              {/* <button
                                className="flex items-center w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-gray-100"
                                onClick={() => handleSendEmail(user)}
                              >
                                <Mail className="mr-2 h-4 w-4" />
                                <span>Send Email</span>
                              </button> */}

                              {/* Activate/Deactivate Button */}
                              <button
                                className={`flex items-center w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                  user.status === true
                                    ? "text-orange-600"
                                    : "text-green-600"
                                }`}
                                onClick={() => handleActivateClick(user)}
                              >
                                {user.status === true ? (
                                  <>
                                    <UserMinus className="mr-2 h-4 w-4" />
                                    <span>Deactivate User</span>
                                  </>
                                ) : (
                                  <>
                                    <UserCheck className="mr-2 h-4 w-4" />
                                    <span>Activate User</span>
                                  </>
                                )}
                              </button>

                              {/* Delete Button */}
                              <button
                                className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                                onClick={() => handleDeleteClick(user)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                <span>Delete User</span>
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>

              <h3 className="text-lg font-medium text-gray-900 text-center mb-4">
                Delete User
              </h3>

              <p className="text-gray-500 text-center mb-6">
                Are you sure you want to delete{" "}
                <strong>{selectedUser.name}</strong>? This action cannot be
                undone.
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  disabled={isDeleting}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmModal}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Activate/Deactivate Confirmation Modal */}
        {showActivateModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div
                className={`flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full ${
                  selectedUser.status === true
                    ? "bg-orange-100"
                    : "bg-green-100"
                }`}
              >
                {selectedUser.status === true ? (
                  <UserMinus className="w-8 h-8 text-orange-600" />
                ) : (
                  <UserCheck className="w-8 h-8 text-green-600" />
                )}
              </div>

              <h3 className="text-lg font-medium text-gray-900 text-center mb-4">
                {selectedUser.status === true
                  ? "Deactivate User"
                  : "Activate User"}
              </h3>

              <p className="text-gray-500 text-center mb-6">
                Are you sure you want to{" "}
                {selectedUser.status === true ? "deactivate" : "activate"}{" "}
                <strong>{selectedUser.name}</strong>?
                {selectedUser.status === true
                  ? " This will revoke their access to the subscription."
                  : " This will restore their access to the subscription."}
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleActivateCancel}
                  disabled={isProcessing}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmActivateModal}
                  disabled={isProcessing}
                  className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                    selectedUser.status === true
                      ? "bg-orange-600 hover:bg-orange-700"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {isProcessing
                    ? selectedUser.status === true
                      ? "Deactivating..."
                      : "Activating..."
                    : selectedUser.status === true
                    ? "Deactivate"
                    : "Activate"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
